import { useState, type FormEvent } from 'react';
import { supabase, SOURCE_BRAND } from './supabase';

type FormState = 'idle' | 'submitting' | 'success' | 'error';

export function App() {
  const [email, setEmail] = useState('');
  // Honeypot field — hidden from humans, bots fill it; we drop those submissions.
  const [honeypot, setHoneypot] = useState('');
  const [state, setState] = useState<FormState>('idle');
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);

    // Silent drop for bots.
    if (honeypot.length > 0) {
      setState('success');
      return;
    }

    if (!email || !email.includes('@')) {
      setError('Pop a valid email in there.');
      return;
    }

    if (!supabase) {
      setError('Hive is warming up. Try again in a moment.');
      return;
    }

    setState('submitting');
    const { error: insertError } = await supabase.from('waitlist_submissions').insert({
      email: email.trim().toLowerCase(),
      source_brand: SOURCE_BRAND,
      user_agent: navigator.userAgent.slice(0, 500),
      referrer: document.referrer.slice(0, 500) || null,
    });

    if (insertError) {
      // Unique violation = already on the list; treat as success.
      if (insertError.code === '23505') {
        setState('success');
      } else {
        setState('error');
        setError('Something broke. Try again?');
      }
      return;
    }

    setState('success');
    setEmail('');
  }

  return (
    <main className="mx-auto flex min-h-screen max-w-2xl flex-col justify-between px-6 py-10 md:px-8">
      <Brand />

      <section className="flex flex-col gap-6">
        <Badge>Launching 2026</Badge>
        <h1
          className="text-5xl leading-[1.02] font-semibold tracking-tight md:text-7xl"
          style={{ fontFamily: 'var(--font-display)' }}
        >
          Every auction,
          <br />
          <span style={{ color: 'var(--bee-honey-deep)' }}>one hive.</span>
        </h1>
        <p
          className="max-w-xl text-lg leading-relaxed md:text-xl"
          style={{ color: 'var(--bee-ink-muted)' }}
        >
          A marketplace where every kind of auction gathers. Estate lots, liquidation finds,
          specialty collections — all in one place, all humming.
        </p>

        {state === 'success' ? (
          <SuccessMessage />
        ) : (
          <form
            onSubmit={(event) => void handleSubmit(event)}
            className="mt-4 flex flex-col gap-3 sm:flex-row sm:gap-2"
          >
            {/* Honeypot — hidden from humans, visible to bots */}
            <label className="sr-only" aria-hidden>
              Leave blank
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={honeypot}
                onChange={(event) => setHoneypot(event.target.value)}
              />
            </label>

            <label className="flex-1">
              <span className="sr-only">Email address</span>
              <input
                type="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="you@gather.email"
                className="w-full rounded-md border bg-white px-4 py-3 text-base placeholder:text-amber-900/40 focus:outline-none"
                style={{ borderColor: 'var(--bee-honey-deep)', color: 'var(--bee-ink)' }}
                disabled={state === 'submitting'}
              />
            </label>
            <button
              type="submit"
              disabled={state === 'submitting'}
              className="rounded-md px-6 py-3 font-semibold transition-colors disabled:opacity-60"
              style={{
                background: 'var(--bee-ink)',
                color: 'var(--bee-cream)',
              }}
            >
              {state === 'submitting' ? 'Sending…' : 'Join the hive'}
            </button>
          </form>
        )}

        {error && (
          <p className="text-sm" style={{ color: '#a10000' }}>
            {error}
          </p>
        )}
      </section>

      <Footer />
    </main>
  );
}

function Brand() {
  return (
    <header className="flex items-center gap-3">
      <BrandMark />
      <span
        className="text-2xl font-semibold tracking-tight"
        style={{ fontFamily: 'var(--font-display)', color: 'var(--bee-ink)' }}
      >
        LotsBee
      </span>
    </header>
  );
}

function BrandMark() {
  return (
    <svg className="h-9 w-9" viewBox="0 0 64 64" aria-hidden>
      <rect width="64" height="64" rx="12" fill="var(--bee-cream)" />
      <g fill="none" stroke="var(--bee-honey-deep)" strokeWidth="2.5" strokeLinejoin="round">
        <polygon points="32,12 42,18 42,30 32,36 22,30 22,18" fill="var(--bee-honey)" />
        <polygon points="20,32 30,38 30,50 20,56 10,50 10,38" />
        <polygon points="44,32 54,38 54,50 44,56 34,50 34,38" />
      </g>
    </svg>
  );
}

function Badge({ children }: { children: React.ReactNode }) {
  return (
    <span
      className="inline-flex w-fit items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold tracking-widest uppercase"
      style={{
        borderColor: 'var(--bee-honey-deep)',
        color: 'var(--bee-amber)',
        background: 'rgba(245, 192, 36, 0.12)',
      }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: 'var(--bee-honey-deep)' }} />
      {children}
    </span>
  );
}

function SuccessMessage() {
  return (
    <div
      className="mt-4 rounded-md border p-4"
      style={{ borderColor: 'var(--bee-honey-deep)', background: 'white' }}
    >
      <p className="font-semibold" style={{ color: 'var(--bee-ink)' }}>
        You're in the hive.
      </p>
      <p className="mt-1 text-sm" style={{ color: 'var(--bee-ink-muted)' }}>
        We'll email when LotsBee opens. No buzz-spam — just the good finds.
      </p>
    </div>
  );
}

function Footer() {
  return (
    <footer className="flex flex-col gap-2 text-sm" style={{ color: 'var(--bee-ink-muted)' }}>
      <p>
        A consumer brand from{' '}
        <a
          href="https://auctisync.com"
          className="font-medium underline-offset-4 hover:underline"
          style={{ color: 'var(--bee-ink)' }}
        >
          AuctiSync
        </a>
        .
      </p>
      <p className="text-xs">© 2026 AuctiSync. All rights reserved.</p>
    </footer>
  );
}
